import React, { useEffect, useState } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import { FaCheck, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { buildAutomationChildTestId, normalizeAutomationTestIdSegment, resolveAutomationTestId } from "../../InputField/testId";

const Stepper = ({ children, activeTab, asLabel, onChange, variant = "primary", testId, numBased = false, hideIndicator = false }) => {
  const resolvedTestId = resolveAutomationTestId(testId);
  const stepChildren = React.Children.toArray(children).filter(Boolean);

  // Each tab's id. Default: 1-based position (unchanged for every existing caller).
  // numBased: the child's explicit `num` prop (e.g. ONBOARDING_STEP). This is what the
  // Individual Plus document-first flow needs — it prepends a Documents pane (num=0) and drives
  // navigation with 0-based ONBOARDING_STEP values, so position-based ids are off by one (and
  // would silently render Documents again when the caller asks for UserDetails). Honouring `num`
  // also stays correct when optional panes (e.g. PayNow for brokers) are not rendered, because the
  // id no longer depends on sibling count. Gated so non-numBased callers are byte-for-byte identical.
  const tabIdOf = (child, index) => (
    numBased && child?.props?.num !== undefined && child?.props?.num !== null
      ? child.props.num
      : index + 1
  );
  const firstTabId = stepChildren.length ? tabIdOf(stepChildren[0], 0) : 1;

  const [activeStep, setActiveStep] = useState(numBased ? (activeTab ?? firstTabId) : (activeTab || 1));
  const [openSteps, setOpenSteps] = useState(() => stepChildren.map(() => true));

  useEffect(() => {
    // numBased must accept 0 (the Documents step), so guard on != null instead of truthiness.
    if (numBased ? activeTab !== undefined && activeTab !== null : activeTab) {
      setActiveStep(activeTab);
      if (asLabel) {
        setOpenSteps((prev) => prev.map((_, index) => tabIdOf(stepChildren[index], index) === activeTab));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, stepChildren.length, asLabel, numBased]);

  const getStepKey = (child, index) => normalizeAutomationTestIdSegment(
    child?.props?.testIdKey || child?.props?.automationKey || child?.props?.num || index + 1,
    String(index + 1)
  );

  const toggleStep = (stepNum) => {
    if (!asLabel) {
      setActiveStep(stepNum);
      onChange?.(stepNum);
    } else {
      setOpenSteps((prev) =>
        prev.map((open, index) => (tabIdOf(stepChildren[index], index) === stepNum ? !open : open))
      );
    }
  };

  const renderStepsIndicator = () => (
    <Nav className="step-nav" data-testid={buildAutomationChildTestId(resolvedTestId, "nav")}>
      {stepChildren
        .map((child, index) => {
          const stepNum = tabIdOf(child, index);
          const stepKey = getStepKey(child, index);
          const isActive = activeStep === stepNum;
          const isCompleted = activeStep > stepNum;

          return (
            <NavItem
              key={index}
              className={classnames("step-item", { dark: variant === "dark", active: isActive, completed: isCompleted })}
              data-testid={buildAutomationChildTestId(resolvedTestId, `step-item-${stepKey}`)}
            >
              <NavLink
                className={classnames("step-link", { dark: variant === "dark", active: isActive, completed: isCompleted })}
                style={{ position: "relative" }}
                data-testid={buildAutomationChildTestId(resolvedTestId, `step-${stepKey}`)}
                // onClick={() => toggleStep(stepNum)}
              >
                <span className="step-indicator" style={{ backgroundColor: isCompleted ? variant === "dark" ? "#000" : "#1678AE" : "#fff" }}>
                  {isCompleted ? <FaCheck color="#fff" size={20} /> : <span className="step-number"></span>}
                </span>
              </NavLink>
            </NavItem>
          );
        })}
    </Nav>
  );

  const renderAccordion = () => (
    <div data-testid={buildAutomationChildTestId(resolvedTestId, "accordion")}>
      {stepChildren
        .map((child, index) => {
          const isOpen = openSteps[index];
          const stepKey = getStepKey(child, index);
          return (
            <div key={index} data-testid={buildAutomationChildTestId(resolvedTestId, `accordion-panel-${stepKey}`)}>
              <div
                className='my-3'
                style={{ fontSize: "16px", fontWeight: "500", cursor: "pointer", display: "flex", alignItems: "center" }}
                onClick={() => toggleStep(tabIdOf(child, index))}
                data-testid={buildAutomationChildTestId(resolvedTestId, `accordion-toggle-${stepKey}`)}
              >
                {isOpen ? <FaChevronDown size={16} style={{ marginRight: "10px" }} /> : <FaChevronRight size={16} style={{ marginRight: "10px" }} />}
                {child.props.label}
              </div>
              {isOpen && child}
            </div>
          );
        })}
    </div>
  );

  return (
    <div className="steps-progress" data-testid={resolvedTestId}>
      {!asLabel && !hideIndicator && renderStepsIndicator()}
      {asLabel ? renderAccordion() :
        <TabContent activeTab={activeStep} data-testid={buildAutomationChildTestId(resolvedTestId, "content")}>
          {stepChildren
            .map((child, index) => {
              const tabId = tabIdOf(child, index);
              const stepKey = getStepKey(child, index);
              return activeStep === tabId
                ? <TabPane key={index} tabId={tabId} data-testid={buildAutomationChildTestId(resolvedTestId, `panel-${stepKey}`)}>{child}</TabPane>
                : null;
            })}
        </TabContent>
      }
    </div>
  );
};

export default Stepper;
